import moment from 'moment';
import { createDentallyAppointmentAsync } from './dentallyApi';
import { patchLeadMetadata } from '../redux/slices/lead';
import { dispatch } from '../redux/store';

const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'center' };

/**
 * Sync a CRM lead's appointment to Dentally.
 * Finds the latest BOOKED appointment, converts to ISO, calls Dentally API.
 * On success patches metadata to 'complete'; on failure keeps 'yes'.
 *
 * @returns {Promise<boolean>} true on success
 */
export async function syncDentallyAppointment({ practiceId, leadId, appointments, leadMetadata, enqueueSnackbar }) {
    // 1. Find latest BOOKED appointment (nearest future first, then most recent)
    const booked = (appointments || [])
        .filter(a => a.status === 'BOOKED')
        .map(a => ({ ...a, _parsed: moment(a.datetime, 'DD-MM-YYYY HH:mm:ss') }))
        .filter(a => a._parsed.isValid())
        .sort((a, b) => a._parsed.valueOf() - b._parsed.valueOf());

    const future = booked.filter(a => a._parsed.isAfter(moment()));
    const target = future[0] || booked[booked.length - 1];

    if (!target) {
        enqueueSnackbar('No booked appointment found to sync', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }

    // 2. Get dentally IDs from metadata
    const patientId = (leadMetadata || []).find(m => m.key === 'dentally_patient_id')?.value || null;
    const practitionerId = (leadMetadata || []).find(m => m.key === 'dentally_practitioner_id')?.value || null;

    if (!practitionerId) {
        enqueueSnackbar('Missing Dentally practitioner — please rebook the appointment via Dentally', { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
        return false;
    }

    // 3. Convert CRM datetime → ISO
    const startISO = target._parsed.toISOString();
    const finishISO = target._parsed.clone().add(30, 'minutes').toISOString();

    // 4. Call Dentally API
    const payload = {
        lead_id: leadId,
        start_time: startISO,
        finish_time: finishISO,
        practitioner_id: practitionerId,
        reason: 'Exam',
        notes: 'Appointment created from DentalSem CRM',
    };
    if (patientId) payload.patient_id = patientId;

    const res = await createDentallyAppointmentAsync(practiceId, payload);

    if (res?.ok || res?.status === 200 || res?.status === 201) {
        dispatch(patchLeadMetadata(practiceId, leadId, [
            { key: 'pending_dentally_sync', value: 'complete' },
        ]));
        enqueueSnackbar('Dentally appointment synced successfully', { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR });
        return true;
    }

    const errorMsg = typeof res?.data === 'string'
        ? res.data
        : res?.data?.detail || res?.data?.message || res?.data?.lead_id || 'Failed to sync Dentally appointment';
    enqueueSnackbar(errorMsg, { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR });
    return false;
}
